import { base44 } from "@/api/base44Client";

import { safeLocalStorage } from '@/lib/browserStorage';
const DRAFT_PREFIX = "drilllog:draft:run:";
const ROD_LENGTH = 6;

function computeToDepth(f) {
  if (f.from_depth == null || f.stick_up == null || f.end_stick_up == null) return null;
  const v = (+f.from_depth) + (+f.stick_up) - (+f.end_stick_up);
  return Number.isFinite(v) ? +v.toFixed(2) : null;
}

function today() {
  const d = new Date();
  const tz = d.getTimezoneOffset() * 60000;
  return new Date(d - tz).toISOString().split("T")[0];
}

/**
 * Push any locally-stored run drafts to the server (failed uploads),
 * then pull a fresh snapshot of all sheets + runs.
 * Returns a summary: { pushed, failed, pending, sheetsCount, runsCount }.
 */
export async function forceSyncRuns() {
  const draftKeys = Array.from({ length: safeLocalStorage.length }, (_, index) => safeLocalStorage.key(index))
    .filter(Boolean)
    .filter((k) => k.startsWith(DRAFT_PREFIX));

  const sheetCache = {};
  const getSheet = async (id) => {
    if (!(id in sheetCache)) {
      try {
        sheetCache[id] = await base44.entities.Sheet.get(id);
      } catch {
        sheetCache[id] = null;
      }
    }
    return sheetCache[id];
  };

  let pushed = 0;
  let failed = 0;
  let skipped = 0;

  for (const key of draftKeys) {
    const m = key.match(/:run:(.+):new$/);
    const sheetId = m?.[1];
    if (!sheetId || sheetId === "unknown") {
      skipped += 1;
      continue;
    }

    let form;
    try {
      form = JSON.parse(safeLocalStorage.getItem(key) || "null");
    } catch {
      form = null;
    }
    if (!form || form.from_depth == null) {
      skipped += 1;
      continue;
    }

    const sheet = await getSheet(sheetId);
    const barrelTableHeight =
      sheet?.barrel_length != null && sheet?.table_height != null
        ? sheet.barrel_length - sheet.table_height
        : null;
    const toDepth = computeToDepth(form);
    const rodsInHole =
      toDepth != null && barrelTableHeight != null
        ? Math.ceil((toDepth - barrelTableHeight) / ROD_LENGTH)
        : null;
    const drilledLen =
      toDepth != null && form.from_depth != null ? toDepth - (+form.from_depth || 0) : null;
    const gainLoss =
      form.recovered != null && drilledLen != null
        ? +(form.recovered - drilledLen).toFixed(2)
        : null;
    const rodsRemaining =
      rodsInHole != null && form.total_rod_count != null
        ? form.total_rod_count - rodsInHole
        : null;

    const payload = {
      record_type: "run",
      sheet_id: sheetId,
      hole_id: sheet?.name || "",
      run_number: form.run_number ?? null,
      from_depth: form.from_depth,
      to_depth: toDepth,
      stick_up: form.stick_up ?? null,
      end_stick_up: form.end_stick_up ?? null,
      recovered: form.recovered ?? null,
      rods_in_hole: rodsInHole,
      total_rod_count: form.total_rod_count ?? null,
      rods_remaining: rodsRemaining,
      gain_loss: gainLoss,
      driller: form.driller || null,
      date: form.date || today(),
      start_time: form.start_time || null,
      end_time: form.end_time || null,
      lithology: form.lithology || null,
      viscosity_check: form.viscosity_check ?? null,
      flow_rate: form.flow_rate ?? null,
      remarks: form.remarks || null,
      photos: form.photos ?? [],
    };

    try {
      await base44.entities.DrillRun.create(payload);
      safeLocalStorage.removeItem(key);
      pushed += 1;
    } catch {
      failed += 1;
    }
  }

  let sheets = [];
  let runs = [];
  try {
    [sheets, runs] = await Promise.all([
      base44.entities.Sheet.list("-created_date"),
      base44.entities.DrillRun.list(),
    ]);
  } catch (e) {
    // ignore pull errors; push results still valid
  }

  return {
    pushed,
    failed,
    pending: skipped,
    sheetsCount: sheets.length,
    runsCount: runs.length,
  };
}